import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#accountHeader = this.closest('header-component');
    if (this.#accountHeader) {
      this.#accountResize = new ResizeObserver(this.#queueAccountPosition);
      this.#accountResize.observe(this.#accountHeader);
      window.addEventListener('resize', this.#queueAccountPosition, { passive: true });
      window.addEventListener('scroll', this.#queueAccountPosition, { passive: true });
      this.#queueAccountPosition();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#accountResize?.disconnect();
    window.removeEventListener('resize', this.#queueAccountPosition);
    window.removeEventListener('scroll', this.#queueAccountPosition);
    cancelAnimationFrame(this.#accountFrame);
    this.#accountFrame = 0;
  }

  // Only the visible header anchors the desktop account dialog, never the
  // header group: that group also contains the campaign banner.
  #accountHeader;
  #accountResize;
  #accountFrame = 0;
  #queueAccountPosition = () => {
    if (this.#accountFrame) return;
    this.#accountFrame = requestAnimationFrame(() => {
      this.#accountFrame = 0;
      if (!this.#accountHeader?.isConnected) return;
      const bottom = Math.max(0, Math.ceil(this.#accountHeader.getBoundingClientRect().bottom));
      const value = `${bottom}px`;
      if (this.#accountHeader.style.getPropertyValue('--mc-account-offset-top') !== value) {
        this.#accountHeader.style.setProperty('--mc-account-offset-top', value);
      }
    });
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
